import { useEffect, useState } from 'react'
import {Heart, Star, Sparkles} from 'lucide-react'
import { lumi } from '../lib/lumi'
import { useAuth } from '../hooks/useAuth'
import CouponCard from './CouponCard'

interface Coupon {
  _id: string
  title: string
  description: string
  brand: string
  location?: string
  code: string
  expiry?: string
  affiliateUrl: string
  discount: string
  featured?: boolean
  clickCount: number
}

interface UserPreference {
  _id?: string
  userId: string
  favoriteCategories?: string[]
  savedCoupons?: string[]
}

export default function PersonalizedRecommendations() {
  const { user, isAuthenticated } = useAuth()
  const [recommendations, setRecommendations] = useState<Coupon[]>([])
  const [userPreferences, setUserPreferences] = useState<UserPreference | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setLoading(false)
      return
    }

    const fetchRecommendations = async () => {
      try {
        // Fetch user preferences
        const prefsResult = await lumi.entities.userPreferences.list({
          where: { userId: user.userId }
        })
        
        const userPrefs = prefsResult.list[0] || null
        setUserPreferences(userPrefs)

        // Fetch all coupons
        const couponsResult = await lumi.entities.coupons.list({
          orderBy: { clickCount: 'desc' },
          limit: 20
        })

        let filteredCoupons = couponsResult.list

        // If user has saved coupons, prioritize them
        if (userPrefs?.savedCoupons && userPrefs.savedCoupons.length > 0) {
          filteredCoupons = filteredCoupons.sort((a, b) => {
            const aIsSaved = userPrefs.savedCoupons?.includes(a._id) ? 1 : 0
            const bIsSaved = userPrefs.savedCoupons?.includes(b._id) ? 1 : 0
            return bIsSaved - aIsSaved
          })
        }

        // Take top 6 for recommendations
        setRecommendations(filteredCoupons.slice(0, 6))
      } catch (error) {
        console.error('Error fetching recommendations:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchRecommendations()
  }, [user, isAuthenticated])

  const toggleSaveCoupon = async (couponId: string) => {
    if (!user || !isAuthenticated) return

    try {
      const currentSaved = userPreferences?.savedCoupons || []
      const isSaved = currentSaved.includes(couponId)
      const newSaved = isSaved
        ? currentSaved.filter(id => id !== couponId)
        : [...currentSaved, couponId]

      if (userPreferences?._id) {
        // Update existing preferences
        await lumi.entities.userPreferences.update(userPreferences._id, {
          savedCoupons: newSaved
        })
      } else {
        // Create new preferences
        await lumi.entities.userPreferences.create({
          userId: user.userId,
          userName: user.userName,
          savedCoupons: newSaved,
          favoriteCategories: [],
          createdAt: new Date().toISOString()
        })
      }

      // Update local state
      setUserPreferences(prev => ({
        ...prev!,
        userId: user.userId,
        savedCoupons: newSaved
      }))
    } catch (error) {
      console.error('Error saving coupon:', error)
    }
  }

  if (!isAuthenticated) {
    return (
      <section className="py-12 bg-gradient-to-br from-green-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-16">
            <Sparkles className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Get Personalized Deal Recommendations
            </h2>
            <p className="text-gray-600 text-lg mb-6">
              Sign in to save your favorite coupons and receive tailored recommendations based on your preferences.
            </p>
          </div>
        </div>
      </section>
    )
  }

  if (loading) {
    return (
      <section className="py-12 bg-gradient-to-br from-green-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading your personalized deals...</p>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className="py-12 bg-gradient-to-br from-green-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Star className="w-8 h-8 text-green-600" />
            <h2 className="text-3xl font-bold text-gray-900">
              Recommended For You, {user?.userName}
            </h2>
          </div>
        </div>

        {recommendations.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">
              No recommendations yet. Start exploring deals to get personalized suggestions!
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {recommendations.map((coupon) => {
              const isSaved = userPreferences?.savedCoupons?.includes(coupon._id)
              return (
                <div key={coupon._id} className="relative">
                  <button
                    onClick={() => toggleSaveCoupon(coupon._id)}
                    className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:shadow-lg transition-all"
                    title={isSaved ? 'Remove from saved' : 'Save for later'}
                  >
                    <Heart
                      className={`w-5 h-5 ${
                        isSaved ? 'fill-red-500 text-red-500' : 'text-gray-400'
                      }`}
                    />
                  </button>
                  <CouponCard {...coupon} />
                </div>
              )
            })}
          </div>
        )}
      </div>
    </section>
  )
}